import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, SimpleChanges } from '@angular/core';
import { Grid } from '../../../core/components/grid/grid.model';
import { GridOptions } from '../../../core/components/grid/grid.types';
import { catchError, EMPTY, from, ReplaySubject, Subject, switchMap, takeUntil } from 'rxjs';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { AcCustTransGet, AcCustTransService, GetAcCustTransParams } from '@app/services/ac-cust-trans.service';
import { ModalService } from '@app/core/services/modal.service';

@Component({
    selector: 'tt-ac-cust-trans',
    templateUrl: './ac-cust-trans.component.html',
    styleUrls: ['./ac-cust-trans.component.css'],
})
export class AcCustTransComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public params?: GetAcCustTransParams;

    public params$ = new ReplaySubject<GetAcCustTransParams>(1);

    public destroy$ = new Subject<void>();

    public get?: AcCustTransGet;

    public grid?: GridOptions;

    public removing = false;

    constructor(
        private acCustTrans: AcCustTransService,
        private modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
        this.params$
            .pipe(
                takeUntil(this.destroy$),
                switchMap((params) => from(this.acCustTrans.getAcCustTrans(params)).pipe(catchError((_) => EMPTY))),
            )
            .subscribe({
                next: (get) => {
                    this.get = get;
                    this.navigator?.setHeading?.(this.get.heading_matching);
                    this.loadGrid();
                },
            });
    }

    private loadGrid() {
        if (!this.get) return;

        if (!this.grid) {
            this.grid = new Grid()
                .setRememberId('w_accusttrans_match_grid')
                .setLoadDataFunction({ method: 2832, parameters: () => this.get })
                .setOptionFunc((data: any) => this.optionFunc(data.data));
        } else {
            this.grid?.gridfunc?.read();
        }
    }

    private optionFunc(data: any) {
        if (data.func && data.func === 'CheckboxBoxClick') {
            if (data.dataItem.is_selected === true) {
                data.dataItem.is_selected_amount_locval_balance = data.dataItem.amount_locval;
            } else {
                data.dataItem.is_selected_amount_locval_balance = 0;
            }

            this.grid?.gridfunc?.refresh();
            this.grid?.gridfunc?.refreshAggregates();
        } else if (data.func && data.func === 'CheckboxHeaderClick') {
            data.data.forEach((dataItem: any) => {
                if (dataItem.is_selected === true) {
                    dataItem.is_selected_amount_locval_balance = dataItem.amount_locval;
                } else {
                    dataItem.is_selected_amount_locval_balance = 0;
                }
            });

            this.grid?.gridfunc?.refresh();
            this.grid?.gridfunc?.refreshAggregates();
        }
    }

    public async removeChanges() {
        if (!!this.get) {
            try {
                this.removing = true;

                const { errorcode, errormessage } = await this.acCustTrans.removeAcCustTransMatching({ ...this.get, records: this.grid?.gridfunc?.getIsSelectedRows().map((row) => ({ accusttransmatch_keyno: row['accusttransmatch_keyno'] })) ?? [] });

                if ('' + errorcode !== '0') {
                    this.modal.show({ type: 'warning', title: 'accusttrans_remove_matching_show_title', translateTitle: true, message: errormessage });
                } else {
                    this.navigator?.back();
                }
            } catch (error) {
                console.error(error);
            } finally {
                this.removing = false;
            }
        }
    }

    private async init(params: GetAcCustTransParams) {
        this.params = params;
        this.params$.next(this.params);
    }

    ngOnInit() {
        if (!!this.navigator) {
            this.navigator.setSize?.({ width: '95vw', height: '98vh' });
            this.init(this.navigator.params as any);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes['params']?.currentValue) {
            this.init(changes['params'].currentValue);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.params$.complete();
    }
}
